import { computeFillAmounts, daysUntil } from './fill-forecast.js';

// Max Fill and Minimum Fill are per-site container properties (percentages of
// capacity), edited on the container's Dispatch pane. They replaced the
// dispatch-wide Maximum Fill % and Minimum Ullage parameters.
const MAX_FILL_PROP = 'max fill';
const MINIMUM_FILL_PROP = 'minimum fill';
const DISPATCH_ENABLED_PROP = 'dispatch enabled';

// BOOLEAN properties arrive as strings. An unset value is treated as enabled so
// a site is only held back when it has been deliberately switched off.
const TRUE_STRINGS = ['1', 'true', 'yes', 'y', 't'];

function isDispatchEnabled(rawValue) {
  if (rawValue == null || String(rawValue).trim() === '') return true;
  return TRUE_STRINGS.includes(String(rawValue).trim().toLowerCase());
}

// Numeric property value for display in the grid, or null when unusable, so a
// column sorts and filters as a number rather than a string.
function numberOrNull(rawValue) {
  const n = parseFloat(rawValue);
  return isNaN(n) ? null : n;
}

// The container endpoints name a property for the user's unit system ("Volume
// (L)" or "Volume (gal)"), so a metric name alone misses for imperial users and
// every fill amount comes out 0. The property master list still carries both
// names, so resolve a metric name to every name it can arrive under (lower
// case, the way the flattened rows key them).
function propertyAliases(masterList, metricName) {
  const aliases = new Set([metricName]);
  for (const p of masterList || []) {
    const names = [p.propertyName, p.propertyNameMetric, p.propertyNameImperial]
      .filter((n) => n != null)
      .map((n) => String(n).toLowerCase());
    if (names.includes(metricName)) names.forEach((n) => aliases.add(n));
  }
  return aliases;
}

// Value of the first key on `row` that matches one of `aliases`, ignoring case.
function valueByAlias(row, aliases) {
  if (!row) return undefined;
  const key = Object.keys(row).find((k) => aliases.has(k.toLowerCase()));
  return key != null ? row[key] : undefined;
}

// What Maximum Fill % defaulted to as a parameter; kept as the fallback for a
// site with no usable Max Fill value so no site loses its fill amounts.
const DEFAULT_MAX_FILL_PCT = 95;

// Max Fill for one site. Only an unreadable value falls back to the default: a
// stored 0 is a real setting (fill this site to 0% -- i.e. nothing to deliver),
// not missing data, so it is honoured. computeFillAmounts floors the resulting
// amount at 0, which is what keeps that case showing 0 rather than a negative.
function maxFillPctFor(rawValue) {
  const pct = parseFloat(rawValue);
  return isNaN(pct) ? DEFAULT_MAX_FILL_PCT : pct;
}

// Minimum Fill for one site, as a percentage of capacity. Unusable or missing
// means 0 -- no minimum level, so the bottom of the delivery range is 0.
function minFillPctFor(rawValue) {
  const pct = parseFloat(rawValue);
  return isNaN(pct) ? 0 : pct;
}

export default {
  // Days from today to the dispatch date — the horizon the predicted fill amount
  // is forecast over. 0 when the date is unset or already past.
  getDispatchForecastDays(state) {
    const params = (state.currentDispatch && state.currentDispatch.parameters) || {};
    return daysUntil(params.dispatchDate);
  },
  // Metric property name -> every name it may be keyed under. See propertyAliases.
  getPropertyAliases(state, getters, rootState, rootGetters) {
    const masterList = rootGetters['properties/getProperties'];
    const names = [
      'capacity', 'volume (l)', 'injection avg',
      MAX_FILL_PROP, MINIMUM_FILL_PROP, DISPATCH_ENABLED_PROP,
    ];
    const map = {};
    names.forEach((n) => { map[n] = propertyAliases(masterList, n); });
    return map;
  },
  getProperties(state, getters, rootState, rootGetters) {
    return (rootGetters['properties/getProperties'] || [])
      .filter((p) => p.containerTypeId == 1 && p.uiShowList == true && p.appliesTo == 'C')
      .map((p) => p.propertyName.toLowerCase())
      .sort();
  },
  getContainers(state, getters, rootState, rootGetters) {
    const source = rootGetters['containers/getContainersV2'];
    if (!source) return [];

    const keepProps = getters.getProperties;
    const numberProps = (rootGetters['properties/getProperties'] || [])
      .filter((p) => p.propertyType == 'NUMBER' || p.propertyType == 'DECIMAL')
      .map((p) => p.propertyName.toLowerCase());

    const forecastDays = getters.getDispatchForecastDays;
    const aliases = getters.getPropertyAliases;

    const containers = JSON.parse(JSON.stringify(source));
    for (const container of containers) {
      const props = container.properties || {};
      const propValue = (name) => valueByAlias(props, aliases[name] || new Set([name]));

      // Fill Amount     = litres needed to top this site up to the max fill level today.
      // Predicted Fill  = the same, but on the dispatch date, with Injection Avg
      //                   drawing the tank down in between. See fill-forecast.js.
      // Max Fill is this site's own ceiling now, not one figure for the run.
      const maxFillPct = maxFillPctFor(propValue(MAX_FILL_PROP));
      const minFillPct = minFillPctFor(propValue(MINIMUM_FILL_PROP));

      const amounts = computeFillAmounts({
        capacity: propValue('capacity'),
        volume: propValue('volume (l)'),
        injectionAvg: propValue('injection avg'),
        minFillPct,
        maxFillPct,
        days: forecastDays,
      });
      // The delivery requirement is a range: minFillAmount brings the site to
      // its minimum volume, fillAmount to its maximum fill level.
      container.minFillAmount = amounts.minFillAmount;
      container.fillAmount = amounts.fillAmount;
      container.predictedMinFillAmount = amounts.predictedMinFillAmount;
      container.predictedFillAmount = amounts.predictedFillAmount;
      container.injectionAvg = amounts.injectionAvg;
      // Carried on the row so a saved dispatch can re-derive its fill amounts
      // without re-reading the property list.
      container.maxFillPct = maxFillPct;
      container.minFillPct = minFillPct;
      // A site switched off for dispatch stays in the picker but is not
      // selected by default, the same treatment Minimum Fill gets.
      container.dispatchEnabled = isDispatchEnabled(propValue(DISPATCH_ENABLED_PROP));

      // These three drive dispatch but carry uiShowList: false, so the flatten
      // loop below (which honours that flag) would drop them. Assigned after
      // that loop so the picker can show them as columns either way.
      const dispatchColumns = {
        [MAX_FILL_PROP]: numberOrNull(propValue(MAX_FILL_PROP)),
        [MINIMUM_FILL_PROP]: numberOrNull(propValue(MINIMUM_FILL_PROP)),
        [DISPATCH_ENABLED_PROP]: container.dispatchEnabled ? 'Yes' : 'No',
      };

      const propNames = Object.keys(props);
      for (const propName of propNames) {
        if (keepProps.includes(propName)) {
          container[propName] = numberProps.includes(propName)
            ? Number(container.properties[propName])
            : container.properties[propName];
        }
      }
      Object.assign(container, dispatchColumns);
      delete container.properties;
    }
    return containers;
  },

  // Containers filtered by the current dispatch parameters: Product, Operator,
  // Area (multi-select) and Maximum DTE. Numeric thresholds exclude sites whose
  // value is missing/non-numeric.
  getFilteredContainers(state, getters) {
    const containers = getters.getContainers;
    const p = (state.currentDispatch && state.currentDispatch.parameters) || {};

    // An empty product list means "no product filter" — same as the old
    // allProducts flag, which older saved dispatches may still carry.
    const rawProducts = p.allProducts ? [] : Array.isArray(p.product) ? p.product : p.product ? [p.product] : [];
    const products = rawProducts.map((v) => String(v).trim()).filter((v) => v !== '');
    const operators = Array.isArray(p.operator) ? p.operator : p.operator ? [p.operator] : [];
    const areas = Array.isArray(p.area) ? p.area : p.area ? [p.area] : [];

    const maxDte = Number(p.maxDte);
    const hasMaxDte = p.maxDte != null && p.maxDte !== '' && !isNaN(maxDte);

    return containers.filter((c) => {
      if (products.length && !products.includes(String(c.product ?? '').trim())) return false;
      if (operators.length && !operators.includes(c.operator)) return false;
      if (areas.length && !areas.includes(c.area)) return false;
      if (hasMaxDte) {
        const dte = parseFloat(c.dte);
        if (isNaN(dte) || dte > maxDte) return false;
      }
      // Minimum Fill does not filter here: it is the bottom of each site's
      // delivery range, not an eligibility test. Dispatch Enabled is what keeps
      // a site out of the run, applied by the picker.
      return true;
    });
  },

  // ---- Dispatch builder ----
  // Distinct Product values found in the loaded container-status data.
  getProducts(state, getters, rootState, rootGetters) {
    const source = rootGetters['containers/getContainersV2'] || [];
    const products = new Set();
    for (const c of source) {
      const props = c.properties || {};
      const key = Object.keys(props).find((k) => k.toLowerCase() === 'product');
      const value = key ? props[key] : null;
      if (value != null && String(value).trim() !== '') {
        products.add(String(value).trim());
      }
    }
    return Array.from(products).sort();
  },
  getSavedDispatches(state) {
    return state.savedDispatches;
  },
  getRecentDispatches(state) {
    // Newest first (highest id), last 5.
    return [...(state.savedDispatches || [])]
      .sort((a, b) => (b.dispatchId || 0) - (a.dispatchId || 0))
      .slice(0, 5);
  },
  getCurrentDispatch(state) {
    return state.currentDispatch;
  },
  getCurrentDispatchParameters(state) {
    return state.currentDispatch.parameters;
  },
  getCurrentDispatchSites(state) {
    return state.currentDispatch.selectedSites;
  },
  // 'tanker' or 'totes'. Everything downstream keys off this rather than
  // re-deriving it, so there is one answer to what kind of run this is.
  getDeliveryMethod(state) {
    const p = (state.currentDispatch && state.currentDispatch.parameters) || {};
    return p.deliveryMethod === 'totes' ? 'totes' : 'tanker';
  },
  getCurrentDispatchSupply(state) {
    return state.currentDispatch.supply || {};
  },
  // Litres required per product across the sites selected for this dispatch,
  // from the same fill amounts the map and the delivery planner work from.
  //
  // `required` is what the sites need today (what the planner loads against);
  // `predicted` is the same figure on the dispatch date, once Injection Avg has
  // drawn the tanks down.
  getSupplySummary(state, getters) {
    const sites = getters.getCurrentDispatchSitesWithCoords || [];
    const rows = new Map();

    for (const site of sites) {
      // Same rule as normalizeProduct() in delivery-planner.js: blank products
      // collapse into one bucket rather than silently merging with a real one.
      const product = String(site.product == null ? '' : site.product).trim() || 'No product';
      const row = rows.get(product) || {
        product,
        siteCount: 0,
        required: 0,
        predicted: 0,
      };
      row.siteCount += 1;
      row.required += Number(site.fillAmount) || 0;
      row.predicted += Number(site.predictedFillAmount) || 0;
      rows.set(product, row);
    }

    return Array.from(rows.values()).sort((a, b) => {
      if (a.product === 'No product') return 1;
      if (b.product === 'No product') return -1;
      return a.product.localeCompare(b.product);
    });
  },
  getCurrentDispatchDeliveryParams(state) {
    return state.currentDispatch.deliveryParams || {};
  },
  // Selected sites enriched with lat/lng. The dispatch/status rows don't carry
  // coordinates, so they're merged in from the containers module (keyed by
  // containerId), which is populated by containers/loadContainers.
  getCurrentDispatchSitesWithCoords(state, getters, rootState, rootGetters) {
    const sites = state.currentDispatch.selectedSites || [];
    const coordSource = rootGetters['containers/getContainers'] || [];
    const coordMap = new Map(coordSource.map((c) => [c.containerId, c]));

    const forecastDays = getters.getDispatchForecastDays;
    const aliases = getters.getPropertyAliases;
    const column = (s, name) => valueByAlias(s, aliases[name] || new Set([name]));

    return sites.map((s) => {
      const match = coordMap.get(s.containerId) || {};
      const latitude = s.latitude != null ? s.latitude : match.latitude;
      const longitude = s.longitude != null ? s.longitude : match.longitude;
      // Fill Amount = litres to deliver to top the site up to the max fill
      // level; this is what the map and delivery planner use as the quantity.
      const fillAmount = Number(s.fillAmount) || 0;
      const ullage = Number(s.ullage != null ? s.ullage : s.Ullage) || 0;

      // Predicted Fill Amount is re-derived rather than trusted from the saved
      // row: the dispatch date can move after the sites were picked, and the
      // forecast has to follow it. Selected rows carry the raw property columns,
      // so this normally succeeds; a row that doesn't (older saved dispatch)
      // falls back to its stored value, then to the current fill amount.
      // Max Fill rides on the saved row (getContainers puts it there). A
      // dispatch saved while it was still a parameter has neither, and falls
      // back to the default that parameter used.
      const maxFillPct = maxFillPctFor(
        s.maxFillPct != null ? s.maxFillPct : s[MAX_FILL_PROP]
      );
      const minFillPct = minFillPctFor(
        s.minFillPct != null ? s.minFillPct : s[MINIMUM_FILL_PROP]
      );

      const recomputed = computeFillAmounts({
        capacity: column(s, 'capacity'),
        volume: column(s, 'volume (l)'),
        injectionAvg: column(s, 'injection avg') != null ? column(s, 'injection avg') : s.injectionAvg,
        minFillPct,
        maxFillPct,
        days: forecastDays,
      });
      const predictedFillAmount =
        recomputed.predictedFillAmount != null
          ? recomputed.predictedFillAmount
          : Number(s.predictedFillAmount) || fillAmount;

      // Bottom of the delivery range, re-derived like the rest; a dispatch
      // saved before the range existed falls back to its stored value, then 0.
      const minFillAmount =
        recomputed.minFillAmount != null
          ? recomputed.minFillAmount
          : Number(s.minFillAmount) || 0;

      return {
        ...s,
        latitude,
        longitude,
        minFillAmount,
        fillAmount,
        ullage,
        predictedMinFillAmount:
          recomputed.predictedMinFillAmount != null
            ? recomputed.predictedMinFillAmount
            : minFillAmount,
        predictedFillAmount,
      };
    });
  },
  // All parameter-matching containers enriched with lat/lng — used by the
  // map-based site picker (which shows every candidate site, not just the
  // currently selected ones).
  getFilteredContainersWithCoords(state, getters, rootState, rootGetters) {
    const filtered = getters.getFilteredContainers;
    const coordSource = rootGetters['containers/getContainers'] || [];
    const coordMap = new Map(coordSource.map((c) => [c.containerId, c]));
    return filtered.map((s) => {
      const match = coordMap.get(s.containerId) || {};
      const latitude = s.latitude != null ? s.latitude : match.latitude;
      const longitude = s.longitude != null ? s.longitude : match.longitude;
      const minFillAmount = Number(s.minFillAmount) || 0;
      const fillAmount = Number(s.fillAmount) || 0;
      const predictedFillAmount = Number(s.predictedFillAmount) || fillAmount;
      const predictedMinFillAmount = Number(s.predictedMinFillAmount) || minFillAmount;
      return {
        ...s,
        latitude,
        longitude,
        minFillAmount,
        fillAmount,
        predictedMinFillAmount,
        predictedFillAmount,
      };
    });
  },
};
